import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import {
  ValuationGridData,
  ValuationPeriodState,
} from "src/app/valuation-page/valuation-page.model";
import {
  faTimes,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";
import { MatTable } from "@angular/material/table";
import { Store } from "@ngrx/store";
import { AppState } from "src/app/store/state/app.state";
import {
  DeleteDpParams,
  SetDpParams,
} from "src/app/store/actions/global-subheader.actions";
import { GridData } from "../tpr.model";
import { AppService } from "src/app/app.service";
import {
  TPR_LOG_ACTION_FEATURES,
  TPR_PAGE,
  TPR_VSPEERS_GRID_COLUMNS,
} from "../tpr-constants";

@Component({
  selector: "app-tpr-peers-grid",
  templateUrl: "./tpr-peers-grid.component.html",
  styleUrls: ["./tpr-peers-grid.component.scss"],
})
export class TprPeersGridComponent {
  @ViewChild("table") table: MatTable<any>;
  @Input() gridData: GridData;
  @Input() showMedian: boolean;
  @Input() showAverage: boolean;
  @Input() orientation: string;
  @Input() ratioId: string;
  @Input() tabType: string;
  @Input() currentTicker;
  @Input() ratioList: Array<any>;
  @Input() periodState: ValuationPeriodState;
  @Input() highlightGAAP = false;
  @Input() visible = false;
  @Input() containerClassName = "";
  @Input() isDataInProgress = false;
  @Input() sortByColumnId = "";
  @Input() currency = "";
  @Output() updateTickerOrder: EventEmitter<any> = new EventEmitter();
  @Output() onDeleteTicker: EventEmitter<any> = new EventEmitter();

  faTimes = faTimes;
  faSortUp = faSortUp;
  faSortDown = faSortDown;
  dataSource: Array<any> = [];
  displayedColumns = [];
  columns = [];
  tableWidth = 520;
  periodWidth = 0;
  sortDirection = "";
  activeSort = "";
  sortKey = "";
  ratioOnly = [];
  index: number;
  selectedRow = "";
  selectedCell = "";
  specialColumns = ["C0", "C1", "C2", "C3"];
  LAST_PRICE_COL_ID = 10;
  RATING_DISTRIBUTION_COL_ID = "rating_distribution";
  columnMappings = {
    companyName: "C1",
    marketCap: "C2",
    ev: "C3",
  };

  // widthConfig = {
  //   C0: "100",
  //   C1: "100",
  //   C2: "100",
  //   C3: "100",
  //   C4: "600",
  //   C5: "400",
  //   C6: "240",
  //   C7: "240",
  //   C8: "240",
  //   C9: "360"
  // };
  constructor(
    private store: Store<AppState>,
    private appService: AppService,
  ) {}

  // ngOnInit() {}

  ngOnChanges(changes: SimpleChanges) {
    //react to changes in gridData
    if (changes.highlightGAAP) {
      if (
        !changes.highlightGAAP.firstChange &&
        changes.highlightGAAP.currentValue !==
          changes.highlightGAAP.previousValue
      ) {
        return;
      }
    }
    if (changes.visible) {
      if (
        !changes.visible.firstChange &&
        changes.visible.currentValue === false
      ) {
        this.selectedRow = "";
        this.selectedCell = "";
        return;
      }
    }
    if (this.gridData && this.gridData.rowData) {
      this.selectedRow = "";
      this.selectedCell = "";
      this.activeSort = "";
      if (this.sortByColumnId) {
        this.activeSort = this.sortByColumnId;
        this.sortDirection = "desc";
      }

      this.dataSource = [...this.gridData.rowData];
      this.columns = this.gridData.columns;
      this.displayedColumns = this.gridData.columns;
    }
  }

  /**
   * It is used to get default set of columns to show on grid based on orientation
   * @param orientation current orientation of the grid
   * * @param tabType current tab peers/company
   */
  getDefaultDisplayedColumns(
    orientation: string,
    tabType: string,
  ): Array<string> {
    if (orientation === "grid") {
      return ["ticker", "companyName", "lastPrice", "marketCap", "ev"];
    }
    if (tabType === "company") {
      return ["metrics"];
    }
    return ["ticker", "companyName"];
  }

  /**
   * It is used to computw the width of period section dislayed on the grid
   * @param periods List of periods
   * @param orientation current orientation of grid
   */
  getPeriodWidth(periods: Array<string>, orientation: string): number {
    if (orientation === "grid") {
      if (periods.length > 2) {
        return periods.length * 100;
      }
      return 200;
    }
    return 100;
  }

  /**
   * It handles ticker deletion from grid and raises onDeleteTicker event for the parent component to handle it
   * @param ticker ticker to be deleted
   */
  deleteTicker(ticker) {
    this.onDeleteTicker.emit(ticker);
  }

  /**
   * It is used to sort given column of the grid in given direction on peers tab
   * @param colKey key of the column to sort
   * @param sortDirection whether to sort asc or desc directio
   */
  sortData(colKey, subColumn = "", sortDirection = "") {
    const colName = this.gridData.colInfo[colKey].label;
    let subColName = "";
    if (subColumn) {
      colKey = `${colKey}-${subColumn}`;
      subColName =
        TPR_VSPEERS_GRID_COLUMNS[colName].subColumnHeaders[subColumn];
    }

    if (sortDirection) {
      this.sortDirection = sortDirection;
    } else {
      if (colKey === this.activeSort) {
        this.sortDirection =
          this.sortDirection === ""
            ? "asc"
            : this.sortDirection === "asc"
              ? "desc"
              : "";
      } else {
        this.sortDirection = "asc";
      }
    }

    const sortOrder =
      this.sortDirection === "asc"
        ? "ascend"
        : this.sortDirection === "desc"
          ? "descend"
          : "default";
    this.appService.logUserAction(
      {},
      this.currentTicker,
      "",
      TPR_LOG_ACTION_FEATURES.peersGridSort,
      {
        column: colName,
        subColumn: subColName,
        sort_direction: sortOrder,
      },
      "",
      TPR_PAGE,
    );

    this.activeSort = colKey;
    switch (colKey) {
      case this.columnMappings["companyName"]:
        if (this.sortDirection === "") {
          this.dataSource = [...this.gridData.rowData];
          this.activeSort = "";
        } else {
          this.dataSource = [
            ...this.dataSource.sort((a, b) => {
              if (this.sortDirection === "asc") {
                return a.C1.localeCompare(b.C1);
              }

              return b.C1.localeCompare(a.C1);
            }),
          ];
        }
        break;
      case this.columnMappings["marketCap"]:
      case this.columnMappings["ev"]:
        if (this.sortDirection === "") {
          this.dataSource = [...this.gridData.rowData];
          this.activeSort = "";
        } else {
          this.dataSource = [
            ...this.dataSource.sort((a, b) => {
              if (this.sortDirection === "asc") {
                if (
                  a[colKey].raw === undefined &&
                  b[colKey].raw === undefined
                ) {
                  return 1;
                }
                if (a[colKey].raw === undefined) {
                  return -1;
                }
                if (b[colKey].raw === undefined) {
                  return 1;
                }
                return a[colKey].raw - b[colKey].raw;
              }
              if (a[colKey].raw === undefined) {
                return 1;
              }
              if (b[colKey].raw === undefined) {
                return -1;
              }
              return b[colKey].raw - a[colKey].raw;
            }),
          ];
        }
        break;
      default:
        //sort for data columns
        if (this.sortDirection === "") {
          this.dataSource = [...this.gridData.rowData];
          this.activeSort = "";
        } else {
          const ratio = colKey.split("-")[0];
          const period = colKey.split("-")[1];
          const sortedData = this.dataSource.sort((a, b) => {
            const aSliData = a[ratio]["subColumns"][period]["raw"];
            const bSliData = b[ratio]["subColumns"][period]["raw"];

            if (this.sortDirection === "asc") {
              if (aSliData === undefined && bSliData === undefined) {
                return 1;
              }
              if (aSliData === undefined) {
                return -1;
              }
              if (bSliData === undefined) {
                return 1;
              }
              if (aSliData === "NM" && bSliData === "NM") {
                return 1;
              }
              if (aSliData === "NM") {
                return -1;
              }
              if (bSliData === "NM") {
                return 1;
              }
              return aSliData - bSliData;
            }
            if (aSliData === undefined) {
              return 1;
            }
            if (bSliData === undefined) {
              return -1;
            }
            if (aSliData === "NM") {
              return 1;
            }
            if (bSliData === "NM") {
              return -1;
            }
            return bSliData - aSliData;
          });
          this.dataSource = [...sortedData];
        }
        break;
    }
    this.updateTickerOrder.emit(this.dataSource);
  }

  /**
   * This function sorts the grid based on the period columns data on company tab.
   * @param colKey string to identify the column on which is being sorted
   * @param sortDirection asc or desc
   */
  companySortData(colKey, sortDirection = "") {
    if (sortDirection) {
      this.sortDirection = sortDirection;
    } else {
      if (colKey === this.activeSort) {
        this.sortDirection =
          this.sortDirection === ""
            ? "asc"
            : this.sortDirection === "asc"
              ? "desc"
              : "";
      } else {
        this.sortDirection = "asc";
      }
    }
    this.activeSort = colKey;
    switch (colKey) {
      default:
        //sort for data columns
        if (this.sortDirection === "") {
          this.dataSource = [...this.ratioList];
          this.activeSort = "";
        } else {
          const period = colKey;
          const sortedData = this.dataSource.sort((a, b) => {
            const aSliData =
              this.gridData.rowData[0][a["ratio"]][period]["raw"];
            const bSliData =
              this.gridData.rowData[0][b["ratio"]][period]["raw"];

            if (this.sortDirection === "asc") {
              if (aSliData === undefined && bSliData === undefined) {
                return 1;
              }
              if (aSliData === undefined) {
                return -1;
              }
              if (bSliData === undefined) {
                return 1;
              }
              if (aSliData === "NM" && bSliData === "NM") {
                return 1;
              }
              if (aSliData === "NM") {
                return -1;
              }
              if (bSliData === "NM") {
                return 1;
              }
              return aSliData - bSliData;
            }
            if (aSliData === undefined) {
              return 1;
            }
            if (bSliData === undefined) {
              return -1;
            }
            if (aSliData === "NM") {
              return 1;
            }
            if (bSliData === "NM") {
              return -1;
            }
            return bSliData - aSliData;
          });
          this.dataSource = [...sortedData];
        }
        break;
    }
  }

  /**
   * returns the index of the obj in rowdata
   * where the company ticker is present
   */
  getCompanyIndex(gridData: ValuationGridData, ticker: string) {
    return gridData.rowData.findIndex((data) => data.ticker === ticker);
  }

  /**
   * It is used to highlight the currently selected row on grid
   * @param rowId identifier of the selected row
   */
  onRowClick(rowId) {
    this.selectedRow = rowId;
    this.selectedCell = "";
  }

  /**
   * It is used to highlight seleted cell on the grid.
   * It is responsible for triggering Formula Bar calls to show formula of the selected cell
   * @param event click event
   * @param data data point on grid that was clicked
   * @param ratio ratio related to the data point
   * @param period period related to the data point
   * @param ticker ticker related to the data point
   * @param rowId identifier of the row of the data point
   * @param isForecast identifier whether the data point is forecast or past
   */
  onCellClick(
    event: MouseEvent,
    data,
    ratio: string,
    period,
    ticker,
    rowId,
    isForecast,
    viewBy = "values",
    cellValue,
    subColId = "",
  ) {
    if (data) {
      event.stopPropagation();
      if (this.selectedCell !== `${ticker}_${ratio}_${subColId}`) {
        const today = new Date();
        const mm = today.getMonth() + 1;
        const dd = today.getDate();
        const yyyy = today.getFullYear();
        isForecast = isForecast === undefined ? true : isForecast;

        const dpId = data[viewBy];
        const growthType = viewBy === "values" ? "Analytics" : "YoY%";
        const dpParams = {
          dp: `${dpId}|${mm}/${dd}/${yyyy}`,
          cut: "STD",
          original_cut: "STD",
          ticker: ticker,
          growthType: growthType,
          shouldLog: true,
          revision: "",
          source: isForecast ? "consensus" : "va actuals",
          isUniversalsliid: true,
          is_universalsliid: true,
          tpr_page: true,
        };

        if (dpParams.growthType === "YoY%") {
          dpParams["historical_focus"] = "VA Actuals";
        }
        if (this.currency && this.currency !== "Base") {
          dpParams["currency"] = this.currency;
        }
        const dpPeriod = period || `CY-${yyyy}`;
        dpParams["period"] = dpPeriod;
        dpParams["extras"] = `${dpId}|${dpPeriod}`;

        this.selectedCell = `${ticker}_${ratio}_${subColId || ""}`;
        this.selectedRow = rowId;
        this.store.dispatch(new SetDpParams(dpParams));
        const label = cellValue.colInfo.label;
        this.appService.logUserAction(
          {},
          this.currentTicker,
          "",
          TPR_LOG_ACTION_FEATURES.peersDatapointClick,
          {
            column: label,
            subColumn: subColId
              ? TPR_VSPEERS_GRID_COLUMNS[label].subColumnHeaders[subColId]
              : "",
            source: dpParams.source,
            ticker: ticker,
            value: cellValue.value,
          },
          "",
          TPR_PAGE,
        );
      }
    } else {
      this.selectedCell = "";
      this.store.dispatch(new DeleteDpParams());
    }
  }
}
